import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { IMOS_STATUSES, IMOS_FILTERABLE_STATUSES, statusClasses, statusTone } from "@/lib/imos";
import { uploadManyImos as uploadMany, fileUrl } from "@/lib/uploads";
import {
  UsersRound, ClipboardList, Upload, Plus, X, Trash2, ChevronRight,
  ShieldAlert, Factory, Check, AlertCircle, FileText, Layers, UserCheck, Search,
  Loader2,
} from "lucide-react";
import PendingQueueBanner, { refreshPending } from "@/components/PendingQueueBanner";
import TeamAssignmentPanel from "@/components/shared/TeamAssignmentPanel";
import TeamSummaryChips, { useProjectTeams } from "@/components/shared/TeamSummaryChips";
import ProjectSearchBar, { ProjectSearchEmpty, projectMatches } from "@/components/shared/ProjectSearchBar";

export default function ImosWorkspacePage() {
  const { isAdmin, roleName, roleType, principal } = useAuth();
  const isManager = isAdmin || roleName === "Imos Manager" || roleType === "director";
  const isExec = roleName === "Imos Executive";

  // Feb 2026 · Smart deep-linking. Consume ?tab=review|orders|factory_status
  // and ?focus_order=<id> from the Pending Actions / Notification click.
  // Feb 2026 (v4) — `tab` is derived DIRECTLY from searchParams so tab flips
  // and URL flips stay in perfect lock-step. Setting the search params is the
  // ONLY way to change tab; the prior model (tab state + sync-effect) leaked
  // a stale searchParams snapshot into the URL and clobbered focus_order that
  // onOpenDetail had just written.
  const [searchParams, setSearchParams] = useSearchParams();
  const ALLOWED_TABS = ["orders", "review", "factory_status"];
  const rawTab = searchParams.get("tab");
  const tab = ALLOWED_TABS.includes(rawTab) ? rawTab : "orders";
  const focusOrderId = searchParams.get("focus_order");
  const setTab = useCallback((next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "orders") params.delete("tab"); else params.set("tab", next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const [orders, setOrders] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(focusOrderId || null);
  const [selected, setSelected] = useState(null);
  const [showDelegate, setShowDelegate] = useState(false);
  const [q, setQ] = useState("");
  // Feb 2026 · workflow tabs (Orders | Review | Factory Status) are driven
  // exclusively from `searchParams` above — no local state, no sync-effect.
  const [reviewQueue, setReviewQueue] = useState([]);
  const [factoryStatus, setFactoryStatus] = useState([]);

  const load = useCallback(async () => {
    try {
      // Feb 2026 bug fix: always fetch the IMOS staff roster so Executives can
      // resolve assignee + reviewer names in the order detail view.
      const [o, e] = await Promise.all([
        api.get("/imos/orders"),
        api.get("/imos/executives"),
      ]);
      setOrders(o.data);
      setExecutives(e.data);
      // Prefer the deep-linked focus_order if it exists in the current feed;
      // otherwise fall back to the previously selected row or the first row.
      if (focusOrderId && o.data.some((x) => x.id === focusOrderId)) {
        setSelectedId(focusOrderId);
      } else if (!selectedId && o.data.length > 0) {
        setSelectedId(o.data[0].id);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [selectedId, focusOrderId]);

  const loadSelected = useCallback(async (id) => {
    if (!id) return setSelected(null);
    try {
      const { data } = await api.get(`/imos/orders/${id}`);
      setSelected(data);
    } catch { setSelected(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSelected(selectedId); }, [selectedId, loadSelected]);

  // Feb 2026 (v4) · Assigned-Orders deep-link — retry scroll until the row's
  // rect actually lands inside the viewport. The earlier single-shot attempt
  // succeeded synchronously but was invalidated by async layout growth from
  // PendingQueueBanner + useProjectTeams team-chips rendering above the list.
  useEffect(() => {
    if (tab !== "orders" || !focusOrderId) return;
    let cancelled = false;
    let flashApplied = false;
    const timers = [];
    const isInView = (el) => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top >= 60 && r.bottom <= vh - 40;
    };
    const attempt = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="imos-row-${focusOrderId}"]`);
      if (!el) return;
      if (!flashApplied) {
        el.classList.add("iz-flash-highlight");
        flashApplied = true;
        timers.push(setTimeout(() => el.classList.remove("iz-flash-highlight"), 2600));
      }
      if (!isInView(el)) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    [0, 400, 900, 1500, 2400].forEach((d) => timers.push(setTimeout(attempt, d)));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [tab, focusOrderId, orders]);
  useEffect(() => {
    const t = setInterval(() => { load(); if (selectedId) loadSelected(selectedId); }, 20000);
    return () => clearInterval(t);
  }, [load, loadSelected, selectedId]);

  // Feb 2026 · lazy-load Review + Factory Status feeds when tab opens
  const [factoryQueue, setFactoryQueue] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const loadReviewQueue = useCallback(async () => {
    setReviewLoading(true);
    try { const { data } = await api.get("/imos/review-queue"); setReviewQueue(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setReviewLoading(false); }
  }, []);
  // Feb 2026 (v4) — Factory tab needs TWO distinct feeds:
  //  * /imos/factory-queue  → the actual sent_to_factory ORDERS (KPI count,
  //    sidebar list, order-detail navigation). Shape: [{id, project_name, ...}]
  //  * /imos/factory-status → the flattened factory_updates activity feed for
  //    the timeline pane. Shape: [{order_id, project_name, at, ...}]
  // Conflating the two (previous fix) leaked update ids into the sidebar and
  // produced 404s on /imos/orders/<update-id>.
  const loadFactoryQueue = useCallback(async () => {
    try { const { data } = await api.get("/imos/factory-queue"); setFactoryQueue(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  const loadFactoryStatus = useCallback(async () => {
    try { const { data } = await api.get("/imos/factory-status"); setFactoryStatus(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => {
    loadFactoryQueue();
    loadFactoryStatus();
    loadReviewQueue();
  }, [loadFactoryQueue, loadFactoryStatus, loadReviewQueue]);
  useEffect(() => {
    if (tab === "review") loadReviewQueue();
    if (tab === "factory_status") { loadFactoryQueue(); loadFactoryStatus(); }
  }, [tab, loadReviewQueue, loadFactoryQueue, loadFactoryStatus]);

  // Feb 2026: sent_to_factory orders NEVER appear in the Assigned Orders tab.
  // They only surface in the "Factory Status" tab.
  // Feb 2026 (bugfix): 'review' status orders NEVER appear in the Assigned Orders tab.
  // They ONLY surface in the "Review" tab (visible to the reviewer only).
  const activeOrders = useMemo(
    () => orders.filter((o) => o.status !== "sent_to_factory" && o.status !== "review"),
    [orders]
  );
  // Feb 2026 (v4) — KPI + sidebar list use factoryQueue (orders).
  // factoryStatus feeds the activity timeline pane inside FactoryStatusTabPanel.
  const factoryOrders = factoryQueue;

  const filtered = useMemo(() => {
    let base = activeOrders;
    if (filter === "unassigned") base = activeOrders.filter((o) => !o.assigned_executive_id);
    else if (filter !== "all") base = activeOrders.filter((o) => o.status === filter);
    return base.filter((o) => projectMatches(o, q, ["flat_no"]));
  }, [activeOrders, filter, q]);

  // Feb 24, 2026 · batch team chips for the IMOS list sidebar.
  const teamsMap = useProjectTeams(filtered.map(o => o.project_id).filter(Boolean));

  const kpis = useMemo(() => ({
    total: activeOrders.length,
    unassigned: activeOrders.filter((o) => !o.assigned_executive_id).length,
    in_progress: activeOrders.filter((o) => o.status === "in_progress").length,
    blocked: activeOrders.filter((o) => o.status === "hold" || o.status === "waiting_for_update").length,
    factory: factoryOrders.length,
  }), [activeOrders, factoryOrders]);

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-6 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            IMOS · {isManager ? "Manager Console" : "Executive Bucket"}
          </p>
          <h1 className="font-display text-3xl text-white mt-1">
            {isManager ? "Production Backlog & Delegation" : "My Drafting Queue"}
          </h1>
        </div>
      </div>

      {isManager && (
        <div className="px-10 pt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Active" value={kpis.total} testid="kpi-active" />
          <Kpi label="Unassigned" value={kpis.unassigned} tone="gold" testid="kpi-unassigned" />
          <Kpi label="In Progress" value={kpis.in_progress} tone="gold" testid="kpi-inprogress" />
          <Kpi label="On Hold / Waiting" value={kpis.blocked} tone="amber" testid="kpi-blocked" />
          <Kpi label="Sent to Factory" value={kpis.factory} tone="emerald" testid="kpi-factory" />
        </div>
      )}

      <PendingQueueBanner testid="pending-queue-imos"/>

      {/* Feb 2026 · workflow tabs */}
      <div className="px-10 pt-4 flex items-center gap-1 border-b border-[#1a1a1a]">
        {[
          { key: "orders", label: "Assigned Orders" },
          { key: "review", label: "Review" },
          { key: "factory_status", label: "Factory Status" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`imos-tab-${t.key}`}
            className={`px-4 py-2 text-[11px] font-mono uppercase tracking-[0.25em] border-b-2 -mb-px transition-all ${
              tab === t.key ? "border-[#d4af37] text-[#d4af37]" : "border-transparent text-gray-500 hover:text-gray-300"
            }`}
          >
            {t.label}
            {t.key === "review" && reviewQueue.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[18px] px-1.5 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37] text-[9px]">{reviewQueue.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "orders" && (
      <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
        <div className="space-y-2">
          <ProjectSearchBar
            value={q}
            onChange={setQ}
            resultCount={filtered.length}
            totalCount={orders.length}
            testid="imos-orders-search"
          />
          {isManager && (
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="iz-input mb-2" data-testid="imos-filter">
              <option value="all">All orders ({activeOrders.length})</option>
              <option value="unassigned">Unassigned ({kpis.unassigned})</option>
              {IMOS_FILTERABLE_STATUSES.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          )}
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">Orders</p>
          {filtered.length === 0 ? (
            q.trim() ? (
              <ProjectSearchEmpty query={q} testid="imos-orders-search-empty" />
            ) : (
              <div className="iz-card p-6 text-center">
                <p className="text-xs font-mono text-gray-500">Nothing here yet.</p>
              </div>
            )
          ) : filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => setSelectedId(o.id)}
              className={`iz-card p-3 w-full text-left transition-colors hover:border-[#d4af37]/40 ${selectedId === o.id ? "border-[#d4af37]" : ""}`}
              data-testid={`imos-row-${o.id}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-white text-sm font-medium truncate">{o.client_name} · {o.city}</p>
                <StatusPill status={o.status} />
              </div>
              <p className="text-[11px] text-gray-500 truncate mt-0.5">{o.project_name}</p>
              {!o.assigned_executive_id && (
                <p className="text-[10px] font-mono text-[#d4af37] mt-1 uppercase tracking-widest">Awaiting delegation</p>
              )}
              {o.project_id && (
                <div className="mt-2">
                  <TeamSummaryChips projectId={o.project_id} slots={teamsMap[o.project_id]} />
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="min-w-0">
          {selected ? (
            <ImosOrderDetail
              order={selected}
              executives={executives}
              isManager={isManager}
              isExec={isExec}
              principalId={principal?.id}
              onDelegate={() => setShowDelegate(true)}
              onRefresh={() => { load(); loadSelected(selectedId); }}
            />
          ) : (
            <div className="iz-card p-12 text-center">
              <ClipboardList className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Select an order to view details.</p>
            </div>
          )}
        </div>
      </div>
      )}

      {tab === "review" && (
        <ReviewTabPanel
          rows={reviewQueue}
          loading={reviewLoading}
          executives={executives}
          principalId={principal?.id}
          isManager={isManager}
          onRefresh={() => { loadReviewQueue(); load(); }}
          focusOrderId={focusOrderId}
          onOpenDetail={(id) => {
            setSelectedId(id);
            // Single setSearchParams call — writes tab+focus_order atomically
            // so no stale-closure sync-effect can clobber the query.
            const next = new URLSearchParams(searchParams);
            next.delete("tab");                    // 'orders' is the default
            next.set("focus_order", id);
            setSearchParams(next, { replace: false });
          }}
        />
      )}

      {tab === "factory_status" && (
        <FactoryStatusTabPanel
          orders={factoryOrders}
          feed={factoryStatus}
          executives={executives}
          principalId={principal?.id}
          isManager={isManager}
          onRefresh={() => { load(); loadFactoryStatus(); }}
        />
      )}

      {showDelegate && selected && (
        <DelegateModal
          order={selected}
          executives={executives}
          onClose={() => setShowDelegate(false)}
          onDone={() => { setShowDelegate(false); load(); loadSelected(selectedId); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone = "gray", testid }) {
  const cls = statusClasses(tone);
  return (
    <div className={`iz-card p-4 border ${cls}`} data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-widest opacity-80">{label}</p>
      <p className="font-display text-3xl mt-1">{value}</p>
    </div>
  );
}

function StatusPill({ status }) {
  const tone = statusTone(status);
  const cls = statusClasses(tone);
  const label = IMOS_STATUSES.find((s) => s.key === status)?.label || status;
  return (
    <span className={`text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${cls} shrink-0`} data-testid={`pill-${status}`}>
      {label}
    </span>
  );
}

function ImosOrderDetail({ order, executives, isManager, isExec, principalId, onDelegate, onRefresh }) {
  const assignedIsMe = order.assigned_executive_id === principalId;
  const reviewerIsMe = order.reviewer_id === principalId;
  const canWrite = isManager || assignedIsMe;
  const canReview = reviewerIsMe;
  const isFactory = order.status === "sent_to_factory";
  const [busySelf, setBusySelf] = React.useState(false);

  const doSelfAssign = async () => {
    if (busySelf) return;
    setBusySelf(true);
    try {
      await api.post(`/imos/orders/${order.id}/self-assign`);
      toast.success("Order self-assigned");
      onRefresh?.();
      refreshPending();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusySelf(false);
    }
  };

  const assignee = executives.find((u) => u.id === order.assigned_executive_id);
  const reviewer = executives.find((u) => u.id === order.reviewer_id);
  const assigneeLabel = assignee
    ? `${assignee.name}${assignee.email ? ` · ${assignee.email}` : ""}`
    : (order.assigned_executive_id ? "Loading…" : "—");
  const reviewerLabel = reviewer
    ? `${reviewer.name}${reviewer.email ? ` · ${reviewer.email}` : ""}`
    : (order.reviewer_id ? "Loading…" : "—");

  return (
    <div className="space-y-4">
      <div className="iz-card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">IMOS Order</p>
            <h2 className="font-display text-2xl text-white mt-1">{order.client_name}{order.city ? ` · ${order.city}` : ""}</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {order.project_name}{order.flat_no ? ` · ${order.flat_no}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusPill status={order.status} />
            {order.status === "review" && order.review_complete && (
              <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-0.5 rounded flex items-center gap-1" data-testid="review-complete-badge-header">
                <Check className="w-3 h-3" /> Review Complete · Sign-off Unlocked
              </span>
            )}
            {isFactory && (
              <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 bg-emerald-500/5 px-2 py-0.5 rounded flex items-center gap-1">
                <Factory className="w-3 h-3" /> Handoff complete
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-4 border-t border-[#1a1a1a]">
          <MetaRow icon={UsersRound} label="Assigned Executive"
                   value={assigneeLabel} testid="meta-assignee" />
          <MetaRow icon={ShieldAlert} label="Reviewer"
                   value={reviewerLabel} testid="meta-reviewer" />
          <MetaRow icon={Layers} label="Materials"
                   value={`${order.material_checklist?.length || 0} tracked`} testid="meta-materials" />
        </div>

        {isManager && (
          <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex gap-2 flex-wrap">
            <button onClick={onDelegate} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="delegate-btn">
              <UsersRound className="w-3.5 h-3.5" /> {order.assigned_executive_id ? "Reassign" : "Delegate"}
            </button>
            {!isFactory && !assignedIsMe && (
              <button
                onClick={doSelfAssign}
                disabled={busySelf}
                className="iz-btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50"
                data-testid="imos-self-assign-btn"
              >
                <UserCheck className="w-3.5 h-3.5" /> Take as Self
              </button>
            )}
          </div>
        )}
      </div>

      <StatusStepper order={order} canWrite={canWrite} canReview={canReview} isManager={isManager} isFactory={isFactory} onRefresh={onRefresh} />
      {order.project_id && <TeamAssignmentPanel projectId={order.project_id} />}
      {/* Feb 2026: Executive keeps edit power on drawings/files/clarifications
          even after Sent to Factory — only the pipeline is locked. */}
      <DrawingsPanel order={order} canWrite={canWrite} onRefresh={onRefresh} />
      <ClarificationsPanel order={order} onRefresh={onRefresh} principalId={principalId} />
      <RelatedConcernsPanel order={order} />
      {order.status === "review" && (reviewerIsMe || isManager) && (
        <ReviewerActionsPanel order={order} onRefresh={onRefresh} canAct={reviewerIsMe} />
      )}
      {isFactory && canWrite && (
        <FactoryFilesPanel order={order} onRefresh={onRefresh} />
      )}
      {isFactory && <TeamUpdatesPanel order={order} />}
      <ExecutionFilesPanel order={order} canWrite={canWrite} onRefresh={onRefresh} />
      <ChecklistPanel order={order} canWrite={canWrite} onRefresh={onRefresh} />
      <HistoryPanel order={order} />
    </div>
  );
}

function MetaRow({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">
        <Icon className="w-3 h-3 text-[#d4af37]" /> {label}
      </div>
      <p className="text-sm text-white">{value}</p>
    </div>
  );
}

function StatusStepper({ order, canWrite, canReview, isManager, isFactory, onRefresh }) {
  const current = order.status;
  const flow = IMOS_STATUSES;

  const advance = async (targetKey, note) => {
    try {
      await api.patch(`/imos/orders/${order.id}/status`, { status: targetKey, note });
      toast.success(`Moved to ${IMOS_STATUSES.find((s) => s.key === targetKey)?.label}`);
      onRefresh();
      refreshPending();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="iz-card p-5" data-testid="status-stepper">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Pipeline</p>
        {isFactory && <p className="text-[10px] font-mono text-emerald-400">Locked · Sent to Factory</p>}
      </div>

      <div className="flex items-center gap-1 overflow-x-auto pb-2">
        {flow.map((s, i) => {
          const isCurrent = s.key === current;
          const isPast = flow.findIndex((x) => x.key === current) > i;
          const tone = isCurrent ? "gold" : isPast ? "emerald" : "gray";
          return (
            <React.Fragment key={s.key}>
              <div className={`px-2.5 py-1 rounded border text-[9px] font-mono uppercase tracking-widest whitespace-nowrap ${statusClasses(tone)}`}
                   data-testid={`step-${s.key}`}>
                {s.label}
              </div>
              {i < flow.length - 1 && <ChevronRight className="w-3 h-3 text-gray-700 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {(canWrite || canReview || isManager) && !isFactory && (
        <div className="mt-4 pt-4 border-t border-[#1a1a1a] flex flex-wrap gap-2">
          {flow.map((s) => {
            if (s.key === current) return null;
            if (s.key === "not_received") return null;
            // Feb 2026 workflow:
            //   • Sign-off is triggered by the ASSIGNED EXECUTIVE (canWrite);
            //     backend requires review_complete when current="review"
            //     — surface a hint below when the gate blocks the click.
            //   • Sent to Factory can be moved from ANY status by the
            //     assigned executive — no more sign_off pre-req.
            const canDo =
              s.key === "sign_off"      ? canWrite :
              s.key === "sent_to_factory" ? canWrite :
              canWrite;
            if (!canDo) return null;
            const gatedByReview = s.key === "sign_off" && current === "review" && !order.review_complete;
            const primary = s.key === "in_progress" || s.key === "sent_to_factory";
            return (
              <button
                key={s.key}
                onClick={() => advance(s.key)}
                disabled={gatedByReview}
                className={`${primary ? "iz-btn-primary text-xs" : "iz-btn-secondary text-xs"} disabled:opacity-40 disabled:cursor-not-allowed`}
                data-testid={`advance-${s.key}`}
                title={gatedByReview ? "Reviewer must mark review complete first" : undefined}
              >
                Move to · {s.label}
                {gatedByReview && <span className="ml-1 text-[9px]">(awaiting review)</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DrawingsPanel({ order, canWrite, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [firing, setFiring] = useState(false);
  const inputRef = React.useRef(null);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadMany(Array.from(files));
      await api.post(`/imos/orders/${order.id}/drawings`, { file_ids: uploaded.map((u) => u.id) });
      toast.success("Drawing uploaded");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const fireBlocker = async () => {
    const msg = window.prompt("Message for CRM (optional):", "");
    if (msg === null) return;
    setFiring(true);
    try {
      await api.post(`/imos/orders/${order.id}/fire-blocker`, { message: msg || null });
      toast.success("Blocker alert sent to CRM & Designer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setFiring(false); }
  };

  return (
    <div className="iz-card p-5" data-testid="drawings-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Engineering Drawings</p>
        {canWrite && (
          <div className="flex gap-2">
            <button
              onClick={fireBlocker}
              disabled={firing}
              className="iz-btn-secondary text-xs flex items-center gap-1.5 hover:border-amber-500 hover:text-amber-400"
              data-testid="fire-blocker">
              <AlertCircle className="w-3.5 h-3.5" /> Fire material blocker
            </button>
            <button onClick={() => inputRef.current?.click()} className="iz-btn-primary text-xs flex items-center gap-1.5" data-testid="upload-drawing">
              <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload drawings"}
            </button>
            <input ref={inputRef} type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf" onChange={(e) => onFiles(e.target.files)} />
          </div>
        )}
      </div>

      {(order.engineering_drawings || []).length === 0 ? (
        <p className="text-xs text-gray-600 italic">No drawings uploaded yet.</p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {order.engineering_drawings.map((d) => (
            <li key={d.file_id} className="flex items-center justify-between px-3 py-2 border border-[#1a1a1a] rounded-md hover:border-[#d4af37]/30" data-testid={`drawing-${d.file_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-3.5 h-3.5 text-[#d4af37] shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{d.original_filename}</p>
                  <p className="text-[10px] font-mono text-gray-600">{new Date(d.uploaded_at).toLocaleString()}</p>
                </div>
              </div>
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="iz-btn-secondary text-[10px] font-mono">Open</a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChecklistPanel({ order, canWrite, onRefresh }) {
  const [items, setItems] = useState(order.material_checklist || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(order.material_checklist || []); }, [order.id, order.material_checklist]);

  const add = () => setItems([...items, { id: `tmp-${Date.now()}`, name: "", finish: "", color: "", is_blocked: false, notes: "" }]);
  const removeAt = (i) => setItems(items.filter((_, idx) => idx !== i));
  const update = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { items: items.map(({ id, name, finish, color, is_blocked, notes }) =>
        ({ id: id?.startsWith?.("tmp-") ? null : id, name, finish, color, is_blocked, notes })) };
      await api.patch(`/imos/orders/${order.id}/checklist`, payload);
      toast.success("Checklist saved");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="iz-card p-5" data-testid="checklist-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Material Checklist</p>
          <p className="text-[10px] font-mono text-gray-600 mt-0.5">Any blocked or missing finish/color auto-fires an alert to CRM.</p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button onClick={add} className="iz-btn-secondary text-xs flex items-center gap-1" data-testid="add-material"><Plus className="w-3 h-3"/>Add row</button>
            <button onClick={save} disabled={saving} className="iz-btn-primary text-xs" data-testid="save-checklist">{saving ? "Saving…" : "Save"}</button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-gray-600 italic">No materials tracked yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border-b border-[#1a1a1a]">
                <th className="text-left py-2 pl-2">Material</th>
                <th className="text-left py-2">Finish</th>
                <th className="text-left py-2">Color</th>
                <th className="text-center py-2">Blocked</th>
                <th className="text-left py-2">Notes</th>
                {canWrite && <th className="py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const missing = !it.finish || !it.color;
                return (
                  <tr key={it.id || i} className="border-b border-[#0f0f0f]" data-testid={`material-row-${i}`}>
                    <td className="py-2 pr-2 pl-2">
                      <Input value={it.name} onChange={(v) => update(i, { name: v })} disabled={!canWrite} placeholder="e.g. Kitchen shutter" />
                    </td>
                    <td className="py-2 pr-2"><Input value={it.finish || ""} onChange={(v) => update(i, { finish: v })} disabled={!canWrite} placeholder="matte / gloss" flag={missing && "amber"} /></td>
                    <td className="py-2 pr-2"><Input value={it.color || ""} onChange={(v) => update(i, { color: v })} disabled={!canWrite} placeholder="hex / name" flag={missing && "amber"} /></td>
                    <td className="py-2 text-center">
                      <button
                        onClick={() => canWrite && update(i, { is_blocked: !it.is_blocked })}
                        disabled={!canWrite}
                        className={`w-8 h-4 rounded-full transition-colors ${it.is_blocked ? "bg-amber-400" : "bg-[#262626]"}`}
                        data-testid={`blocked-toggle-${i}`}
                      >
                        <span className={`block w-3 h-3 rounded-full bg-white transition-transform ${it.is_blocked ? "translate-x-4" : "translate-x-0.5"}`}></span>
                      </button>
                    </td>
                    <td className="py-2 pr-2"><Input value={it.notes || ""} onChange={(v) => update(i, { notes: v })} disabled={!canWrite} placeholder="—" /></td>
                    {canWrite && (
                      <td className="py-2 pl-2">
                        <button onClick={() => removeAt(i)} className="p-1 text-gray-500 hover:text-red-400" data-testid={`remove-material-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Input({ value, onChange, disabled, placeholder, flag }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className={`w-full bg-transparent border-b border-[#1a1a1a] focus:border-[#d4af37] text-xs text-white placeholder-gray-600 outline-none px-1 py-1 disabled:text-gray-400 ${flag === "amber" ? "border-amber-500/40" : ""}`}
    />
  );
}

function HistoryPanel({ order }) {
  if (!order.status_history || order.status_history.length === 0) return null;
  return (
    <div className="iz-card p-5" data-testid="history-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Timeline</p>
      <ol className="relative border-l border-[#1a1a1a] ml-2 pl-4 space-y-3">
        {[...order.status_history].reverse().map((h, i) => (
          <li key={h.at ? `${h.at}-${h.status}` : `sh-${i}`}>
            <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-[#d4af37] mt-1" />
            <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{IMOS_STATUSES.find((s) => s.key === h.status)?.label || h.status}</p>
            <p className="text-[10px] font-mono text-gray-500">{new Date(h.at).toLocaleString()}</p>
            {h.note && <p className="text-xs text-gray-300 mt-0.5">{h.note}</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function DelegateModal({ order, executives, onClose, onDone }) {
  const [assigneeId, setAssigneeId] = useState(order.assigned_executive_id || "");
  const [reviewerId, setReviewerId] = useState(order.reviewer_id || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!assigneeId) { setErr("Assigned Executive is required"); return; }
    if (reviewerId && assigneeId === reviewerId) { setErr("Reviewer must be different from the assigned executive"); return; }
    setSaving(true);
    try {
      const body = { assigned_executive_id: assigneeId };
      if (reviewerId) body.reviewer_id = reviewerId;
      await api.post(`/imos/orders/${order.id}/delegate`, body);
      toast.success(reviewerId ? "Order delegated" : "Order delegated · Reviewer can be assigned later");
      onDone();
      refreshPending();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reviewerOptions = useMemo(
    () => executives.filter((u) => u.id !== assigneeId),
    [executives, assigneeId],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="delegate-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="iz-card w-full max-w-lg p-8 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Delegate</p>
        <h2 className="font-display text-2xl text-white mb-6">Assign {order.project_name}</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Assigned Executive</label>
            <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className="iz-input" required data-testid="assignee-select">
              <option value="">Select executive…</option>
              {executives.map((u) => <option key={u.id} value={u.id}>{u.name}{u.imos_role_label ? ` · ${u.imos_role_label}` : ""}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Reviewer <span className="text-gray-600 normal-case tracking-normal font-sans">(optional — assign later from the Review tab)</span></label>
            <select value={reviewerId} onChange={(e) => setReviewerId(e.target.value)} className="iz-input" data-testid="reviewer-select">
              <option value="">— Assign later —</option>
              {reviewerOptions.map((u) => <option key={u.id} value={u.id}>{u.name}{u.imos_role_label ? ` · ${u.imos_role_label}` : ""}</option>)}
            </select>
            <p className="text-[10px] font-mono text-gray-600 mt-1">Reviewer cannot be the assignee.</p>
          </div>
          {err && <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/5 rounded-md px-3 py-2">{err}</div>}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#1a1a1a]">
            <button type="button" onClick={onClose} className="iz-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-1.5" data-testid="submit-delegate"><Check className="w-3.5 h-3.5"/>{saving ? "Delegating…" : "Confirm"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}

// Feb 2026 — Clarifications thread between IMOS Executive & Interior Designer.
function ClarificationsPanel({ order, onRefresh, principalId }) {
  const [thread, setThread] = useState(order.clarifications || []);
  const [seenMap, setSeenMap] = useState(order.clarifications_seen || {});
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]); // uploaded {id, original_filename}
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => { setThread(order.clarifications || []); }, [order.id, order.clarifications]);
  useEffect(() => { setSeenMap(order.clarifications_seen || {}); }, [order.id, order.clarifications_seen]);

  // Mark clarifications as seen for the current user whenever the order or the
  // thread updates. The endpoint just stamps clarifications_seen[uid]=now.
  useEffect(() => {
    if (!order?.id || !principalId) return;
    api.post(`/imos/orders/${order.id}/clarifications/mark-seen`)
      .then(({ data }) => {
        setSeenMap((prev) => ({ ...prev, [principalId]: data.seen_at }));
      })
      .catch(() => { /* silent */ });
  }, [order?.id, thread.length, principalId]);

  const onFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadMany(Array.from(fileList));
      setFiles((prev) => [...prev, ...uploaded]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!message.trim()) { toast.error("Message required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/imos/orders/${order.id}/clarifications`, {
        message: message.trim(),
        file_ids: files.map((f) => f.id),
      });
      setThread((prev) => [...prev, data]);
      setMessage(""); setFiles([]);
      toast.success("Clarification posted · Counterpart notified");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  // A message is "seen" when some OTHER user's seen_at is newer than the message.
  const seenBy = (msg) => {
    return Object.entries(seenMap || {})
      .filter(([uid, at]) => uid !== msg.by_user_id && at && at >= msg.at);
  };

  return (
    <div className="iz-card p-5" data-testid="clarifications-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Clarifications · {thread.length}
        </p>
        <p className="text-[10px] font-mono text-gray-500">Two-way thread with the linked Interior Designer</p>
      </div>

      <div className="max-h-72 overflow-y-auto space-y-2 mb-4 pr-1" data-testid="clarifications-list">
        {thread.length === 0 ? (
          <p className="text-xs text-gray-600 italic py-4 text-center">No clarifications yet — start the conversation below.</p>
        ) : thread.map((c) => {
          const mine = c.by_user_id === principalId;
          const witnesses = mine ? seenBy(c) : [];
          return (
          <div key={c.id}
                className={`border rounded-md p-3 ${mine ? "border-[#d4af37]/40 bg-[#0f0c05]" : "border-[#1a1a1a] bg-[#0a0a0a]"}`}
                data-testid={`clarification-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[11px] font-mono text-gray-300">
                {c.by_user_name} <span className="text-gray-600">· {c.by_role}</span>
              </p>
              <p className="text-[10px] font-mono text-gray-600">{new Date(c.at).toLocaleString()}</p>
            </div>
            <p className="text-xs text-white whitespace-pre-wrap">{renderWithMentions(c.message)}</p>
            {(c.files || []).length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-1.5" data-testid={`clarification-files-${c.id}`}>
                {c.files.map((f) => (
                  <li key={f.file_id}>
                    <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 border border-[#262626] rounded text-[10px] font-mono text-gray-300 hover:text-[#d4af37] hover:border-[#d4af37]/40">
                      <FileText className="w-2.5 h-2.5"/>{f.original_filename}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {mine && witnesses.length > 0 && (
              <p className="text-[10px] font-mono text-emerald-400/80 mt-1.5 flex items-center gap-1"
                  data-testid={`clarification-seen-${c.id}`}
                  title={witnesses.map(([, at]) => new Date(at).toLocaleString()).join(", ")}>
                <Check className="w-2.5 h-2.5"/> seen · {new Date(witnesses[0][1]).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
              </p>
            )}
          </div>
          );
        })}
      </div>

      <div className="border-t border-[#1a1a1a] pt-3 space-y-2">
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                   placeholder="Ask a question, share a note, request rework..."
                   className="iz-input" data-testid="clarification-message"/>
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" data-testid="clarification-attachments">
            {files.map((f) => (
              <li key={f.id}
                   className="inline-flex items-center gap-1 px-2 py-1 border border-[#d4af37]/40 rounded text-[10px] font-mono text-[#d4af37]">
                <FileText className="w-2.5 h-2.5"/>{f.original_filename}
                <button onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                         className="ml-1 text-gray-500 hover:text-red-400"><X className="w-2.5 h-2.5"/></button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-between gap-2">
          <button onClick={() => inputRef.current?.click()}
                   className="iz-btn-secondary text-xs flex items-center gap-1.5"
                   data-testid="clarification-attach">
            <Upload className="w-3 h-3"/>{uploading ? "Uploading…" : "Attach file"}
          </button>
          <input ref={inputRef} type="file" hidden multiple accept="application/pdf,image/*,.dwg,.dxf,.ppt,.pptx"
                  onChange={(e) => onFiles(e.target.files)}/>
          <button onClick={submit} disabled={busy || !message.trim()}
                   className="iz-btn-primary text-xs flex items-center gap-1.5"
                   data-testid="clarification-submit">
            {busy ? "Posting…" : "Post message"}
          </button>
        </div>
      </div>
    </div>
  );
}

const EXEC_FILE_SLOTS = [
  { key: "final_2d", label: "Final 2D Drawings" },
  { key: "final_3d", label: "Final 3D Renders" },
  { key: "ppt",      label: "Presentation (PPT)" },
  { key: "quotation", label: "Quotation PDF" },
];

// Feb 2026 — IMOS Executive uploads the 4 execution files consumed by the
// downstream Site / Execution team.
function ExecutionFilesPanel({ order, canWrite, onRefresh }) {
  const [uploading, setUploading] = useState(null);
  const inputRefs = {
    final_2d: React.useRef(null), final_3d: React.useRef(null),
    ppt: React.useRef(null), quotation: React.useRef(null),
  };
  const current = order.execution_files || {};

  const upload = async (kind, fileList) => {
    if (!fileList?.[0]) return;
    setUploading(kind);
    try {
      const [uploaded] = await uploadMany([fileList[0]]);
      await api.post(`/imos/orders/${order.id}/execution-files`, {
        kind, file_id: uploaded.id,
      });
      toast.success(`${EXEC_FILE_SLOTS.find((s) => s.key === kind).label} uploaded`);
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploading(null); }
  };

  return (
    <div className="iz-card p-5" data-testid="execution-files-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Execution Files · Handoff</p>
        <p className="text-[10px] font-mono text-gray-500">Uploaded here → visible to Site / Execution team</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {EXEC_FILE_SLOTS.map((slot) => {
          const cur = current[slot.key];
          const isUp = uploading === slot.key;
          return (
            <div key={slot.key} className="border border-[#1a1a1a] rounded-md p-3" data-testid={`exec-file-slot-${slot.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-mono text-gray-400 uppercase tracking-widest">{slot.label}</p>
                {cur && <FileText className="w-3 h-3 text-emerald-400"/>}
              </div>
              {cur ? (
                <div className="flex items-center justify-between">
                  <a href={fileUrl(cur.file_id)} target="_blank" rel="noreferrer"
                      className="text-xs text-white truncate hover:text-[#d4af37]" data-testid={`exec-file-link-${slot.key}`}>
                    {cur.original_filename}
                  </a>
                  {canWrite && (
                    <button onClick={() => inputRefs[slot.key].current?.click()}
                             className="iz-btn-secondary text-[10px] font-mono flex items-center gap-1"
                             data-testid={`exec-file-replace-${slot.key}`}>
                      <Upload className="w-2.5 h-2.5"/>Replace
                    </button>
                  )}
                </div>
              ) : (
                canWrite ? (
                  <button onClick={() => inputRefs[slot.key].current?.click()}
                           className="iz-btn-primary text-xs w-full flex items-center justify-center gap-1.5"
                           data-testid={`exec-file-upload-${slot.key}`}>
                    <Upload className="w-3 h-3"/>{isUp ? "Uploading…" : "Upload"}
                  </button>
                ) : (
                  <p className="text-[11px] text-gray-600 italic">Not uploaded yet.</p>
                )
              )}
              <input ref={inputRefs[slot.key]} type="file" hidden
                      accept="application/pdf,image/*,.ppt,.pptx,.dwg,.dxf"
                      onChange={(e) => upload(slot.key, e.target.files)}/>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// Highlight `@Name` tokens in gold. Non-destructive — passes through non-mention text.
function renderWithMentions(text) {
  if (!text) return null;
  const parts = String(text).split(/(@[A-Za-z][A-Za-z0-9_]{1,30})/g);
  return parts.map((p, i) =>
    p.startsWith("@")
      ? <span key={`m-${i}-${p}`} className="text-[#d4af37] font-medium" data-testid="mention-token">{p}</span>
      : <React.Fragment key={`t-${i}`}>{p}</React.Fragment>
  );
}

// Feb 2026 — Related concerns cross-view. Read-only surface that mirrors any
// client concern raised on the same project so the IMOS + Design team see
// escalations alongside their clarifications thread.
function RelatedConcernsPanel({ order }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!order?.id) return;
    api.get(`/imos/orders/${order.id}/related-concerns`)
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, [order?.id]);

  if (!loaded) return null;
  if (rows.length === 0) return null; // hide when nothing linked — no noise

  return (
    <div className="iz-card p-5" data-testid="related-concerns-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Related Client Concerns · {rows.length}
        </p>
        <p className="text-[10px] font-mono text-gray-500">Cross-linked from this project</p>
      </div>
      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.id}
                className={`border rounded-md p-3 ${c.status === "resolved" ? "border-emerald-500/30 bg-[#071008]" : "border-[#d4af37]/30 bg-[#0d0b06]"}`}
                data-testid={`related-concern-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-white">{c.subject}</p>
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${c.status === "resolved" ? "border-emerald-500/40 text-emerald-400" : "border-[#d4af37]/40 text-[#d4af37]"}`}>
                {c.status}
              </span>
            </div>
            <p className="text-[11px] font-mono text-gray-500">
              Raised {new Date(c.created_at).toLocaleString()} · By {c.raised_by_email}
            </p>
            <p className="text-xs text-gray-300 mt-1.5 whitespace-pre-wrap">{c.description}</p>
            {(c.replies || []).length > 0 && (
              <div className="mt-3 pl-3 border-l-2 border-[#d4af37]/30 space-y-1.5">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#d4af37]">
                  CRM Replies · {c.replies.length}
                </p>
                {c.replies.map((r) => (
                  <div key={r.id} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded p-2">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className="text-[10px] font-mono text-gray-300">
                        {r.by_user_name} <span className="text-gray-600">· {r.by_role}</span>
                      </p>
                      <p className="text-[10px] font-mono text-gray-600">{new Date(r.at).toLocaleString()}</p>
                    </div>
                    <p className="text-xs text-white whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}



// ---------- Feb 2026 · Reviewer Actions ----------
function ReviewerActionsPanel({ order, onRefresh, canAct }) {
  const [uploading, setUploading] = useState(false);
  const [marking, setMarking] = useState(false);
  const doUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await uploadMany(files);
      await api.post(`/imos/orders/${order.id}/review-files`, { file_ids: uploaded.map((r) => r.id) });
      toast.success(`${files.length} review file(s) uploaded`);
      onRefresh?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const markComplete = async () => {
    setMarking(true);
    try {
      await api.post(`/imos/orders/${order.id}/review-complete`);
      toast.success("Review marked complete · Sign-off unlocked for the Executive");
      onRefresh?.();
      refreshPending();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setMarking(false); }
  };
  const files = order.review_files || [];
  return (
    <div className="iz-card p-5 border-[#d4af37]/40" data-testid="reviewer-actions-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Reviewer Actions</p>
      <h3 className="font-display text-lg text-white mt-1">
        {order.review_complete ? "Review complete" : "Review this package"}
      </h3>
      <p className="text-[11px] text-gray-500 mt-1">
        Upload review artifacts, then mark complete to unlock Sign-off for the assigned Executive.
      </p>
      <div className="mt-3 space-y-2">
        {files.map((f) => (
          <div key={f.file_id} className="flex items-center justify-between border border-[#262626] rounded px-3 py-1.5 text-xs" data-testid={`review-file-${f.file_id}`}>
            <span className="text-gray-300 truncate">{f.original_filename}</span>
            <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" className="text-[#d4af37] font-mono text-[10px] uppercase tracking-widest">Open</a>
          </div>
        ))}
        {files.length === 0 && <p className="text-[11px] text-gray-600 italic">No review files uploaded yet.</p>}
      </div>
      {canAct && !order.review_complete && (
        <div className="flex items-center gap-2 mt-4">
          <label className="iz-btn-secondary text-xs flex items-center gap-1.5 cursor-pointer" data-testid="review-upload-btn">
            {uploading ? <span>Uploading…</span> : (<><Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> Upload files</>)}
            <input type="file" multiple onChange={doUpload} className="hidden" />
          </label>
          <button
            onClick={markComplete}
            disabled={marking}
            className="iz-btn-primary text-xs flex items-center gap-1.5 disabled:opacity-40"
            data-testid="review-complete-btn"
          >
            <Check className="w-3.5 h-3.5" />
            {marking ? "Marking…" : "Mark Review Complete"}
          </button>
        </div>
      )}
      {order.review_complete && (
        <p className="mt-3 text-emerald-400 text-xs font-mono uppercase tracking-widest" data-testid="review-complete-badge">
          ✓ Completed {order.review_complete_at ? new Date(order.review_complete_at).toLocaleString() : ""}
        </p>
      )}
    </div>
  );
}


// ---------- Feb 2026 · Factory Files (IMOS Exec uploads for factory handoff) ----------
function FactoryFilesPanel({ order, onRefresh }) {
  const slots = [
    { key: "two_d", label: "2D Drawings" },
    { key: "three_d", label: "3D Views" },
    { key: "ppt", label: "PPT" },
    { key: "quotation", label: "Quotation" },
  ];
  const files = order.factory_files || {};
  const doUpload = async (slotKey, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const uploaded = await uploadMany([file]);
      await api.post(`/imos/orders/${order.id}/factory-files`, {
        [`${slotKey}_file_id`]: uploaded[0].id,
      });
      toast.success(`${slotKey.replace('_', ' ').toUpperCase()} uploaded`);
      onRefresh?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { e.target.value = ""; }
  };
  return (
    <div className="iz-card p-5" data-testid="factory-files-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Factory Handoff Files</p>
      <h3 className="font-display text-lg text-white mt-1">Shared with Production / Procurement / Purchase</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
        {slots.map((s) => {
          const f = files[s.key];
          return (
            <div key={s.key} className="border border-[#262626] rounded p-3" data-testid={`factory-slot-${s.key}`}>
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{s.label}</p>
              {f ? (
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-300 truncate">{f.original_filename}</span>
                  <a href={fileUrl(f.file_id)} target="_blank" rel="noreferrer" className="text-[#d4af37] font-mono text-[10px] uppercase tracking-widest ml-2">Open</a>
                </div>
              ) : (
                <p className="text-[11px] text-gray-600 italic mt-1">Not uploaded</p>
              )}
              <label className="iz-btn-secondary text-xs mt-2 flex items-center justify-center gap-1.5 cursor-pointer" data-testid={`factory-upload-${s.key}`}>
                <Upload className="w-3.5 h-3.5" strokeWidth={1.5} />
                {f ? "Replace" : "Upload"}
                <input type="file" onChange={(e) => doUpload(s.key, e)} className="hidden" />
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Feb 2026 · Review Tab ----------
function ReviewTabPanel({ rows, loading, executives, principalId, isManager, onRefresh, focusOrderId, onOpenDetail }) {
  const [assigning, setAssigning] = useState({}); // orderId → reviewerId being edited
  const [busyId, setBusyId] = useState(null);

  const saveReviewer = async (orderId, reviewerId) => {
    setBusyId(orderId);
    try {
      await api.post(`/imos/orders/${orderId}/assign-reviewer`, { reviewer_id: reviewerId || "" });
      toast.success(reviewerId ? "Reviewer assigned" : "Reviewer cleared");
      onRefresh?.();
      refreshPending();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  // Auto-scroll to the deep-linked row (Pending Actions / Notification click).
  useEffect(() => {
    if (!focusOrderId) return;
    const el = document.querySelector(`[data-testid="review-row-${focusOrderId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("iz-flash-highlight");
      const t = setTimeout(() => el.classList.remove("iz-flash-highlight"), 2600);
      return () => clearTimeout(t);
    }
  }, [focusOrderId, rows]);

  return (
    <div className="px-10 py-6" data-testid="imos-review-tab">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-1">Review Queue</p>
      <h2 className="font-display text-xl text-white mb-4">
        {isManager ? "Orders awaiting or under review — assign a reviewer inline" : "Review pool — every IMOS user tracks the same list"}
      </h2>
      {loading && rows.length === 0 && (
        <div className="iz-card p-10 text-center" data-testid="imos-review-loading">
          <Loader2 className="w-5 h-5 text-[#d4af37] animate-spin mx-auto mb-2"/>
          <p className="text-xs font-mono uppercase tracking-widest text-gray-500">Loading review queue…</p>
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="iz-card p-10 text-center" data-testid="imos-review-empty">
          <p className="text-sm text-gray-500">Nothing here yet.</p>
        </div>
      )}
      <div className="space-y-3">
        {rows.map((o) => {
          const reviewer = executives.find((u) => u.id === o.reviewer_id);
          return (
            <div key={o.id} className="iz-card p-4" data-testid={`review-row-${o.id}`}>
              <div className="flex items-start justify-between gap-4">
                <button
                  type="button"
                  onClick={() => onOpenDetail?.(o.id)}
                  className="text-left min-w-0 flex-1 hover:text-[#d4af37] transition-colors"
                  data-testid={`review-row-open-${o.id}`}
                >
                  <p className="text-white text-sm truncate">{o.client_name} · {o.city}</p>
                  <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                    Reviewer: <span className="text-white">{reviewer ? reviewer.name : (o.reviewer_id ? "…" : "— Unassigned —")}</span>
                    {o.review_complete && <span className="ml-3 text-emerald-400">✓ Review Complete</span>}
                  </p>
                  <p className="text-[10px] font-mono text-gray-600 mt-1">{(o.review_files || []).length} review file(s) · open to review</p>
                </button>
                {isManager && (
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      className="iz-input text-xs min-w-[220px]"
                      value={assigning[o.id] ?? (o.reviewer_id || "")}
                      onChange={(e) => setAssigning({ ...assigning, [o.id]: e.target.value })}
                      data-testid={`review-reviewer-select-${o.id}`}
                    >
                      <option value="">— Unassigned —</option>
                      {executives.filter((u) => u.id !== o.assigned_executive_id).map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                    <button
                      className="iz-btn-primary text-xs disabled:opacity-40"
                      onClick={() => saveReviewer(o.id, assigning[o.id] ?? (o.reviewer_id || ""))}
                      disabled={busyId === o.id}
                      data-testid={`review-reviewer-save-${o.id}`}
                    >{busyId === o.id ? "Saving…" : "Save"}</button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ---------- Feb 2026 · Team Updates Panel (inside order detail) ----------
function TeamUpdatesPanel({ order }) {
  const teams = ["Production Manager", "Procurement Manager", "Purchase Manager", "Purchase Executive"];
  const updates = order.factory_updates || [];
  const latestByTeam = teams.map((t) => {
    const own = updates.filter((u) => u.role === t);
    return { team: t, latest: own[own.length - 1] || null, count: own.length };
  });
  return (
    <div className="iz-card p-5" data-testid="team-updates-panel">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">
        Latest Factory Updates
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {latestByTeam.map(({ team, latest, count }) => (
          <div
            key={team}
            className={`border rounded p-3 ${latest ? "border-[#d4af37]/40 bg-[#d4af37]/5" : "border-[#262626]"}`}
            data-testid={`team-latest-${team.toLowerCase().replace(/ /g, '-')}`}
          >
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{team}</p>
              <span className="text-[10px] font-mono text-gray-500">{count} upd.</span>
            </div>
            {latest ? (
              <>
                <p className="text-sm text-white mt-1">{latest.status}</p>
                {latest.note && <p className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{latest.note}</p>}
                <p className="text-[10px] text-gray-600 mt-1">
                  {latest.by_name} · {new Date(latest.at).toLocaleString()}
                </p>
              </>
            ) : (
              <p className="text-[11px] text-gray-600 italic mt-1">Awaiting update</p>
            )}
          </div>
        ))}
      </div>
      {updates.length > 0 && (
        <details className="border-t border-[#1a1a1a] pt-3" data-testid="team-updates-full-log">
          <summary className="text-[10px] font-mono uppercase tracking-widest text-gray-500 cursor-pointer hover:text-[#d4af37]">
            Full update log ({updates.length})
          </summary>
          <div className="space-y-2 mt-3 max-h-80 overflow-y-auto">
            {[...updates].reverse().map((u) => (
              <div key={u.id} className="border border-[#262626] rounded px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">{u.role}</p>
                  <p className="text-[10px] text-gray-600">{new Date(u.at).toLocaleString()}</p>
                </div>
                <p className="text-sm text-gray-200 mt-1">{u.status}</p>
                {u.note && <p className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">{u.note}</p>}
                <p className="text-[10px] text-gray-600 mt-1">by {u.by_name}</p>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


// ---------- Feb 2026 · Factory Status Tab (workspace) ----------
function FactoryStatusTabPanel({ orders, feed, executives, principalId, isManager, onRefresh }) {
  const [selectedId, setSelectedId] = useState(orders[0]?.id || null);
  const [selectedFull, setSelectedFull] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!selectedId && orders.length) setSelectedId(orders[0].id);
  }, [orders, selectedId]);

  const loadSelectedFull = useCallback(async (oid) => {
    if (!oid) { setSelectedFull(null); return; }
    try {
      const { data } = await api.get(`/imos/orders/${oid}`);
      setSelectedFull(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { loadSelectedFull(selectedId); }, [selectedId, loadSelectedFull]);
  useEffect(() => {
    const t = setInterval(() => { if (selectedId) loadSelectedFull(selectedId); }, 20000);
    return () => clearInterval(t);
  }, [selectedId, loadSelectedFull]);

  const filtered = useMemo(
    () => (orders || []).filter((o) => projectMatches(o, q, ["flat_no"])),
    [orders, q]
  );

  return (
    <div className="px-10 py-6 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start" data-testid="imos-factory-status-tab">
      <div className="space-y-2">
        <ProjectSearchBar
          value={q}
          onChange={setQ}
          resultCount={filtered.length}
          totalCount={orders.length}
          testid="factory-status-search"
        />
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">
          Sent to Factory ({orders.length})
        </p>
        {orders.length === 0 && (
          <div className="iz-card p-6 text-center" data-testid="imos-factory-status-empty">
            <Factory className="w-6 h-6 text-gray-700 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Nothing at Factory yet.</p>
          </div>
        )}
        {filtered.map((o) => (
          <button
            key={o.id}
            onClick={() => setSelectedId(o.id)}
            data-testid={`factory-order-row-${o.id}`}
            className={`iz-card p-3 w-full text-left transition-colors hover:border-[#d4af37]/40 ${selectedId === o.id ? "border-[#d4af37]" : ""}`}
          >
            <p className="text-white text-sm truncate">{o.client_name} · {o.city}</p>
            <p className="text-[11px] text-gray-500 truncate">{o.project_name}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
              {(o.factory_updates || []).length} update(s)
            </p>
          </button>
        ))}
      </div>

      <div className="min-w-0">
        {selectedFull ? (
          <ImosOrderDetail
            order={selectedFull}
            executives={executives}
            isManager={isManager}
            isExec={true}
            principalId={principalId}
            onDelegate={() => {}}
            onRefresh={() => { loadSelectedFull(selectedId); onRefresh?.(); }}
          />
        ) : selectedId ? (
          <div className="iz-card p-12 text-center">
            <p className="text-sm text-gray-500">Loading…</p>
          </div>
        ) : (
          <div className="iz-card p-12 text-center">
            <Factory className="w-8 h-8 text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              {orders.length === 0
                ? "No orders have reached factory yet."
                : "Select an order from the left to view team status and edit files."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
